import { computed, ref, type Ref } from "vue";

export function useClientPagination<T>(source: Ref<T[]>, initialSize = 25) {
  const currentPage = ref(1);
  const pageSize = ref(initialSize);

  const totalElements = computed(() => source.value.length);

  const totalPages = computed(() => {
    if (totalElements.value === 0) return 0;
    return Math.ceil(totalElements.value / pageSize.value);
  });

  const paginatedItems = computed(() => {
    if (totalElements.value === 0) return [];
    const start = (currentPage.value - 1) * pageSize.value;
    return source.value.slice(start, start + pageSize.value);
  });

  const rangeStart = computed(() => {
    if (totalElements.value === 0) return 0;
    return (currentPage.value - 1) * pageSize.value + 1;
  });

  const rangeEnd = computed(() => {
    return Math.min(currentPage.value * pageSize.value, totalElements.value);
  });

  const previous = () => {
    if (currentPage.value > 1) currentPage.value--;
  };

  const next = () => {
    if (currentPage.value < totalPages.value) currentPage.value++;
  };

  const onPageSizeChange = () => {
    currentPage.value = 1;
  };

  const resetPage = () => {
    currentPage.value = 1;
  };

  return {
    currentPage,
    pageSize,
    totalElements,
    totalPages,
    paginatedItems,
    rangeStart,
    rangeEnd,
    previous,
    next,
    onPageSizeChange,
    resetPage,
  };
}
